import { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { cn } from '../../../lib/cn';
import { Check, X, ChevronDown, ChevronRight } from 'lucide-react';
import ClozeWord from './ClozeWord';

/**
 * Cloze review component — sentence-on-top, input below.
 *
 * Props:
 *   sentenceWithBlank   — "The fine ___ around his eyes..."
 *   firstLetter         — "c" (legacy; used as fallback if maskPattern absent)
 *   remainingLength     — 7  (legacy; used as fallback if maskPattern absent)
 *   maskPattern         — [1,0,1,1,0,1,0,1] one entry per char of acceptedSurfaceForm,
 *                         1 = visible, 0 = blank. Source of truth for the slot
 *                         grid; legacy fields are derived only when this is
 *                         missing (in-flight pre-graduated-cloze payloads).
 *   acceptedSurfaceForm — "crinkles"
 *   definition          — primary definition (revealed on first miss)
 *   sentenceHtml        — preserved paragraph HTML from source (or null)
 *   onAnswer            — ({ text, elapsed_ms, attempt }) => Promise<{ correct, retry?, hint?, definition?, correct_answer, rating }>
 *
 * Hint-retry flow (spec §4.8): on attempt 1 wrong, backend returns
 * `{ retry: true, hint: { mask_pattern, prefix, remaining }, definition }`
 * instead of applying FSRS. We re-render the slot grid using the updated
 * mask (two additional letters revealed) + show the definition, and let
 * the student submit attempt 2. Attempt 2 always resolves the card
 * (good/hard on correct, again on wrong). Falls back to prefix/remaining
 * for clients that haven't reloaded since before this branch.
 */
export default function Cloze({
  sentenceWithBlank,
  firstLetter,
  remainingLength,
  maskPattern,
  acceptedSurfaceForm,
  definition,
  pos,
  sentenceHtml,
  onAnswer,
  // Input placeholder text. Default surfaces the prompt for callers that
  // don't render their own heading above (e.g. ReviewActivity); onboarding
  // Stage 4 passes "" since Stage4Cloze.jsx already renders a heading
  // above and the duplication looked sloppy.
  placeholder = 'Type the whole word...',
  // Legacy props for backwards compatibility
  lemmaHintLength,
  // Controlled props (used by OnboardingActivity to mirror the student's
  // typed answer + grader result to the teacher's observer view via
  // progress_state). When omitted, internal useState is the fallback.
  text: controlledText,
  result: controlledResult,
  onTextChange,
  onResult,
  // Controlled hint-open state. Lifts the §3.6 collapsible hint panel
  // up to the parent for live-session bidirectional sync. Optional;
  // when omitted (solo review usage) internal useState is the fallback.
  hintOpen: controlledHintOpen,
  onHintOpenChange,
}) {
  const [localText, setLocalText] = useState('');
  const [localResult, setLocalResult] = useState(null); // null | { tier, correctWord }
  // Retry shape:
  //   miss : { kind: 'miss', prefix, remaining, definition }
  //   typo : { kind: 'typo', correctSpelling, definition }
  const [retry, setRetry] = useState(null);
  // §3.10 — attempt history shown above the input. Each entry is a
  // string of what the student typed for an incorrect or retry attempt.
  // Cleared when the card changes (sentenceWithBlank effect).
  const [attempts, setAttempts] = useState([]);
  // §3.6 collapsible hint panel: closed by default per card, opens on
  // student request. We track whether it was *ever* opened during this
  // card so we can pass `hint_used: true` to the grader for analytics.
  // Doesn't affect FSRS rating — see spec §3.6.
  const [localHintOpen, setLocalHintOpen] = useState(false);
  const isHintOpenControlled = controlledHintOpen !== undefined;
  const hintOpen = isHintOpenControlled ? !!controlledHintOpen : localHintOpen;
  const setHintOpen = (next) => {
    const value = typeof next === 'function' ? next(hintOpen) : next;
    if (typeof onHintOpenChange === 'function') onHintOpenChange(value);
    if (!isHintOpenControlled) setLocalHintOpen(value);
  };
  const hintUsedRef = useRef(false);
  const text = controlledText !== undefined ? controlledText : localText;
  const result = controlledResult !== undefined ? controlledResult : localResult;
  const setText = (v) => {
    if (typeof onTextChange === 'function') onTextChange(v);
    else setLocalText(v);
  };
  const setResult = (r) => {
    if (typeof onResult === 'function') onResult(r);
    else setLocalResult(r);
  };
  const startTime = useRef(Date.now());
  const attemptRef = useRef(1);
  const inputRef = useRef(null);
  const sentenceRef = useRef(null);

  // Hint state: attempt-1 uses the original first_letter + remaining_length;
  // attempt-2 :miss uses the expanded prefix the backend returned;
  // :typo and :variant retries don't add letter hints (their reveal lives in
  // the retry message below the input) so they fall back to the original
  // first_letter / remaining_length and keep the blank visible — otherwise
  // hintLetter / hintLength evaluate to undefined and the .vocab-cloze-blank
  // span collapses to an empty pill.
  const hintLetter = retry?.prefix ?? (firstLetter || null);
  const hintLength = retry?.remaining ?? (remainingLength ?? (lemmaHintLength ? lemmaHintLength - 1 : 6));

  // Active mask drives the per-letter slot grid. Priority order:
  //   1. retry payload's mask_pattern (attempt-2 :miss reveal)
  //   2. prop maskPattern (current card's freshly-built mask)
  //   3. legacy derivation from firstLetter + remainingLength (pre-graduated
  //      payloads): [1, 0, 0, ..., 0]
  const activeMask = (() => {
    if (Array.isArray(retry?.maskPattern)) return retry.maskPattern;
    if (Array.isArray(maskPattern)) return maskPattern;
    const total = (firstLetter ? 1 : 0) + (typeof hintLength === 'number' ? hintLength : 0);
    if (total <= 0) return null;
    return [1, ...Array(total - 1).fill(0)];
  })();

  useEffect(() => {
    startTime.current = Date.now();
    attemptRef.current = 1;
    setText('');
    setResult(null);
    setRetry(null);
    setAttempts([]);
    setHintOpen(false);
    hintUsedRef.current = false;
    inputRef.current?.focus();
  }, [sentenceWithBlank]);

  const toggleHint = () => {
    setHintOpen((open) => {
      if (!open) hintUsedRef.current = true;
      return !open;
    });
  };

  const handleSubmit = async () => {
    if (!text.trim() || result) return;

    const elapsed = Date.now() - startTime.current;
    const attempt = attemptRef.current;
    const res = await onAnswer({
      text: text.trim(),
      elapsed_ms: elapsed,
      attempt,
      hint_used: hintUsedRef.current,
    });

    // Backend signaled retry — expand the hint, keep the input live for one more shot.
    if (res?.retry) {
      attemptRef.current = 2;
      // §3.10 — record the attempt so the student sees what they tried.
      setAttempts((prev) => [...prev, text.trim()]);
      if (res.kind === 'typo') {
        // Spelling-miss path: carry the student's correct letters into the
        // slot grid (backend sends a mask with no extra reveal) so the cloze
        // updates to e.g. "T_TTERS" — they only have to fix the letter(s)
        // they missed, rather than being handed the whole word.
        setRetry({
          kind: 'typo',
          maskPattern: Array.isArray(res.hint?.mask_pattern) ? res.hint.mask_pattern : null,
          definition: res.definition || definition || null,
        });
      } else if (res.kind === 'variant') {
        // Wrong-form path: don't reveal the surface outright — the student got
        // the right idea but typed a form (lemma, different inflection, or
        // synonym in lemma form) that doesn't fit the slot. Carry over the
        // letters they got right so the shared prefix stays visible and the
        // differing form reads as blanks, then prompt them to re-read the
        // sentence and produce the form that actually fits.
        setRetry({
          kind: 'variant',
          maskPattern: Array.isArray(res.hint?.mask_pattern) ? res.hint.mask_pattern : null,
          definition: res.definition || definition || null,
        });
      } else {
        setRetry({
          kind: 'miss',
          maskPattern: Array.isArray(res.hint?.mask_pattern) ? res.hint.mask_pattern : null,
          prefix: res.hint?.prefix || (firstLetter || ''),
          remaining: res.hint?.remaining ?? Math.max((remainingLength ?? 0) - 2, 0),
          definition: res.definition || definition || null,
        });
      }
      setText('');
      // Re-focus + reset timer for the second attempt's elapsed_ms reading.
      startTime.current = Date.now();
      setTimeout(() => inputRef.current?.focus(), 50);
      return res;
    }

    // Dispatch on the backend grader's `kind` (exact | variant | typo | miss),
    // not just on `rating`. The rating alone collapses "got it" and "you wrote
    // a near-miss spelling that we accepted as :hard" into the same bucket,
    // which made misspellings render as "Got it on the second try — fested"
    // (showing the typo as if it were the correct word). Using kind keeps the
    // four cases distinct and picks the right displayed word for each.
    const kind = res?.kind;
    const rating = res?.rating;
    const correctWord = res?.correct_answer || acceptedSurfaceForm;
    const studentAnswer = text.trim();

    let tier;
    let wordToShow;
    let targetWord;
    if (kind === 'exact') {
      // Student typed the correct surface (or lemma). rating === 2 means they
      // recovered on attempt 2; rating === 3 means first-shot.
      tier = rating === 2 ? 'hard_recovered' : 'good';
      wordToShow = studentAnswer;
    } else if (kind === 'synonym') {
      // Right meaning, different word (§3.9c). Counted as :good by the grader.
      // Render the student's word inside the blank — they showed they got the
      // concept — and surface the target lemma in the feedback line so they
      // still pick up the specific word the sentence used.
      tier = rating === 2 ? 'synonym_recovered' : 'synonym_good';
      wordToShow = studentAnswer;
      targetWord = correctWord;
    } else if (kind === 'variant') {
      // Right word, wrong form (legible vs legibly). Show the accepted form
      // so the student learns the precise inflection the sentence wanted.
      tier = 'hard';
      wordToShow = correctWord;
    } else if (kind === 'typo') {
      // Right word, misspelled. Show the correct spelling — NOT what the
      // student typed — so the reveal teaches the right letters.
      tier = 'hard_typo';
      wordToShow = correctWord;
    } else if (kind === 'miss') {
      tier = 'again';
      wordToShow = correctWord;
    } else {
      // Legacy fallback (older payloads without `kind`) — infer from rating.
      if (rating === 1) {
        tier = 'again';
        wordToShow = correctWord;
      } else if (rating === 2) {
        tier = 'hard_recovered';
        wordToShow = studentAnswer;
      } else {
        tier = 'good';
        wordToShow = studentAnswer;
      }
    }

    // Push the typed answer onto the attempts strip for anything other than
    // a clean hit — exact (good / hard_recovered) and synonym (counts as
    // right) both render the student's word in the blank, so the strikethrough
    // strip would just duplicate it.
    if (
      tier !== 'good' &&
      tier !== 'hard_recovered' &&
      tier !== 'synonym_good' &&
      tier !== 'synonym_recovered'
    ) {
      setAttempts((prev) => [...prev, studentAnswer]);
    }

    setResult({
      tier,
      correctWord: wordToShow,
      targetWord: targetWord || null,
      studentAnswer,
      definition: definition || null,
    });
    return res;
  };

  // Render the sentence with the blank
  const renderSentence = () => {
    // Defensive guard — a cloze payload without `sentence_with_blank`
    // means upstream gave us a non-cloze item routed here by mistake
    // (e.g. an existing review session whose queue stored format:"cloze"
    // before review_sessions_controller started persisting the actual
    // returned format from ReviewItemBuilder). Render a graceful skip
    // affordance instead of crashing on `.split` of undefined.
    if (!sentenceWithBlank && !sentenceHtml) {
      return (
        <div className="text-center py-6 space-y-3">
          <p className="text-sm text-gray-500 dark:text-gray-400 italic">
            Couldn't build this question — skip to move on.
          </p>
          <button
            type="button"
            onClick={() =>
              onAnswer({ text: '', elapsed_ms: 0, attempt: 2, hint_used: false })
            }
            className="text-sm text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300 font-medium"
          >
            Skip this card
          </button>
        </div>
      );
    }

    // Mode A: HTML-preserved (chapter source)
    if (sentenceHtml && !result) {
      const slotHtml = renderMaskedBlankHtml(activeMask, acceptedSurfaceForm, hintLetter);
      const totalLetters = (acceptedSurfaceForm?.length) ?? ((hintLetter ? 1 : 0) + (hintLength ?? 0));
      const ariaLabel = `${totalLetters}-letter word starting with ${hintLetter || ''}`;
      return (
        <div
          ref={sentenceRef}
          className="vocab-cloze-sentence"
          dangerouslySetInnerHTML={{
            __html: sentenceHtml.replace(
              /___/g,
              `<span class="vocab-cloze-blank vocab-cloze-blank--slots" aria-label="${ariaLabel}">${slotHtml}</span>`
            ),
          }}
        />
      );
    }

    // Mode B: plain-text. Split on paragraph boundaries (\n+) and render
    // each as its own <p> with book typography — same shape as
    // ContextPassage but specialized to inline the cloze blank into
    // whichever paragraph contains `___`. Shares §3.11's typography for
    // multi-paragraph context_sentence values.
    const paragraphs = sentenceWithBlank
      .split(/\n+/)
      .map((p) => p.trim())
      .filter(Boolean);

    const totalLetters = (acceptedSurfaceForm?.length) ?? ((hintLetter ? 1 : 0) + (hintLength ?? 0));
    const blankNode = result ? (
      // Reveal: occupy what was the blank with the correct word, keeping the
      // soft-blue accent the blank had so the eye reads continuity. Tier
      // controls only the text color; the blue tint stays constant.
      <span
        className={cn(
          'vocab-cloze-blank',
          'font-semibold',
          (result.tier === 'good' || result.tier === 'synonym_good') ? 'text-gray-900 dark:text-white' :
          (result.tier === 'hard' || result.tier === 'hard_recovered' || result.tier === 'synonym_recovered') ? 'text-gray-700 dark:text-gray-300' :
          'text-gray-500 dark:text-gray-400'
        )}
      >
        {result.correctWord}
      </span>
    ) : (
      <span
        className="vocab-cloze-blank vocab-cloze-blank--slots"
        aria-label={`${totalLetters}-letter word starting with ${hintLetter || ''}`}
      >
        <ClozeWord
          surface={acceptedSurfaceForm || ((hintLetter || '') + ' '.repeat(hintLength || 0))}
          mask={activeMask}
        />
      </span>
    );

    return (
      <div className="vocab-cloze-sentence">
        {paragraphs.map((para, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === paragraphs.length - 1;
          const parts = para.split('___');
          const containsBlank = parts.length >= 2;

          return (
            <p
              key={idx}
              className="vocab-cloze-plain"
              style={{
                textIndent: isFirst ? '2em' : '2em',
                marginBottom: isLast ? 0 : '0.5em',
              }}
            >
              {containsBlank ? (
                <>
                  {parts[0]}
                  {blankNode}
                  {parts.slice(1).join('___')}
                </>
              ) : (
                para
              )}
            </p>
          );
        })}
      </div>
    );
  };

  return (
    <div className="space-y-8 max-w-2xl mx-auto">
      {/* Sentence — left-aligned so multi-paragraph passages read as
          book typography rather than ragged-center prose. Single-line
          passages still look fine because the wrapping <div> sits inside
          a max-w-2xl mx-auto column. */}
      <div className="text-left">
        {renderSentence()}
      </div>

      {/* §3.10 — Previous attempts on this card, shown between passage and
          input so the student sees what they've already tried. Strikethrough
          + muted to read as discarded. Stays visible after the card resolves
          so the student keeps the full history of guesses next to the
          correct-answer feedback below. */}
      {attempts.length > 0 && (
        <div className="max-w-md mx-auto flex flex-wrap gap-2 justify-center">
          {attempts.map((a, i) => (
            <span
              key={`${a}-${i}`}
              className="text-xs text-gray-400 dark:text-gray-500 line-through font-mono"
            >
              {a}
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      {!result && (
        <div className="max-w-xs mx-auto">
          <input
            ref={inputRef}
            type="text"
            autoFocus
            spellCheck={false}
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            data-gramm="false"
            data-gramm_editor="false"
            // Stable key for the live-session typing mirror (useInputBroadcast
            // + LiveTypingOverlay) so the teacher's overlay locks to this field.
            data-live-input-key="cloze"
            className="vocab-cloze-input"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
              }
            }}
            placeholder={placeholder}
            aria-label="Type the whole word"
          />
        </div>
      )}

      {/* §3.6 collapsible hint — POS + definition, available from attempt 1.
          Closed by default per card; opening logs `hint_used: true` but
          doesn't penalise FSRS. Hidden once the card resolves and during
          the retry window (where the dedicated retry hint takes over). */}
      {!result && !retry && (definition || pos) && (
        <div className="max-w-md mx-auto">
          <button
            type="button"
            onClick={toggleHint}
            aria-expanded={hintOpen}
            // pointer-events-auto: re-enable this toggle when the activity is
            // observer-locked (pointer-events-none on the wrapper) so the
            // teacher can open/close the clue too — it syncs via peer_intent.
            className="pointer-events-auto flex items-center gap-1 mx-auto text-xs font-medium text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
          >
            {hintOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            {hintOpen ? 'Hide clue' : 'Show clue'}
          </button>
          {hintOpen && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.15 }}
              className="mt-2 px-4 py-3 rounded-md border border-gray-200 dark:border-gray-700/60 bg-gray-50 dark:bg-gray-800/40 text-center space-y-1"
            >
              {pos && (
                <div className="text-[11px] uppercase tracking-wide text-gray-400 dark:text-gray-500 font-medium">
                  {pos}
                </div>
              )}
              {definition && (
                <div className="text-sm text-gray-700 dark:text-gray-300 italic">
                  {definition}
                </div>
              )}
            </motion.div>
          )}
        </div>
      )}

      {/* Retry hint window (attempt 1 wrong, attempt 2 pending). */}
      {retry && !result && (
        <motion.div
          initial={{ opacity: 0, y: -4 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center max-w-md mx-auto space-y-2"
        >
          {retry.kind === 'typo' && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              So close — a letter or two off. The letters you got right are
              filled in; fix the blank and type the whole word again.
            </p>
          )}
          {retry.kind === 'variant' && (
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Right idea, wrong form — re-read the sentence and try the form
              that fits.
            </p>
          )}
          {retry.definition && (
            <p className="text-sm text-gray-500 dark:text-gray-400 italic">
              {retry.definition}
            </p>
          )}
        </motion.div>
      )}

      {/* Feedback */}
      {result && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center space-y-3"
        >
          <ResultIcon tier={result.tier} />

          {result.tier === 'good' && (
            <p className="text-base text-gray-700 dark:text-gray-300">
              The word was{' '}
              <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                {result.correctWord}
              </span>
              .
            </p>
          )}

          {result.tier === 'hard_recovered' && (
            <p className="text-base text-gray-700 dark:text-gray-300">
              Got it on the second try — the word was{' '}
              <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                {result.correctWord}
              </span>
              .
            </p>
          )}

          {result.tier === 'synonym_good' && (
            <p className="text-base text-gray-700 dark:text-gray-300">
              Wrong word, right meaning!{' '}
              <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                {result.correctWord}
              </span>{' '}
              fits — the sentence used{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {result.targetWord}
              </span>
              .
            </p>
          )}

          {result.tier === 'synonym_recovered' && (
            <p className="text-base text-gray-700 dark:text-gray-300">
              Right meaning on the second try —{' '}
              <span className="font-semibold text-emerald-700 dark:text-emerald-400">
                {result.correctWord}
              </span>{' '}
              fits; the sentence used{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {result.targetWord}
              </span>
              .
            </p>
          )}

          {result.tier === 'hard' && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Right word, different form — the word was{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {result.correctWord}
              </span>
              .
            </p>
          )}

          {result.tier === 'hard_typo' && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Spelling miss — the word is{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {result.correctWord}
              </span>
              .
            </p>
          )}

          {result.tier === 'again' && (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Not quite — the word was{' '}
              <span className="font-medium text-gray-700 dark:text-gray-300">
                {result.correctWord || 'something else'}
              </span>
              .
            </p>
          )}

          {/* Reveal panel — definition (and POS, if known) shown beneath
              the feedback line on miss-ish tiers. B2: pos surfaces alongside
              the gloss so students can connect the word form to its part
              of speech. */}
          {result.definition && (result.tier === 'hard' || result.tier === 'hard_typo' || result.tier === 'again') && (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic max-w-md mx-auto pt-1">
              {pos && (
                <span className="not-italic font-medium text-gray-500 dark:text-gray-400 mr-1">
                  {pos}.
                </span>
              )}
              {result.definition}
            </p>
          )}
        </motion.div>
      )}
    </div>
  );
}

// HTML-string variant of ClozeWord for Mode A (sentenceHtml is injected
// via dangerouslySetInnerHTML, so we can't drop React components into it).
// Must emit visually identical pills to ClozeWord + ClozeSlot. Uses inline
// `style="..."` for the same reason the React components do — guaranteed
// rendering, immune to stylesheet / Tailwind JIT issues.
// `text-indent: 0` is CRITICAL — the cloze blank sits inside chapter <p>s
// that carry text-indent: 2em for prose typography. text-indent inherits
// and applies to the first line of any descendant block formatting context
// (including inline-flex). Without this override the letter inside each
// pill gets shifted 2em right and overflows the pill on the right side.
const SLOT_BASE_STYLE =
  'display:inline-flex;align-items:center;justify-content:center;' +
  'vertical-align:middle;width:1.5em;height:1.7em;border-radius:0.25em;' +
  'line-height:1;box-sizing:border-box;padding:0;margin:0;text-align:center;' +
  'text-indent:0;border:1px solid rgba(96,165,250,0.5);';
const SLOT_REVEALED_STYLE =
  SLOT_BASE_STYLE + 'font-weight:700;background:rgba(59,130,246,0.12);';
const SLOT_BLANK_STYLE =
  SLOT_BASE_STYLE + 'background:rgba(59,130,246,0.04);color:transparent;';
const WORD_WRAPPER_STYLE =
  'display:inline-flex;align-items:center;vertical-align:middle;gap:3px;text-indent:0;';

function renderMaskedBlankHtml(mask, surface, firstLetterFallback) {
  const haveMask = Array.isArray(mask) && mask.length > 0;
  const haveSurface = typeof surface === 'string' && surface.length > 0;
  const esc = (s) =>
    String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const slotHtml = (ch, revealed) => {
    if (revealed) {
      return `<span style="${SLOT_REVEALED_STYLE}">${esc(ch)}</span>`;
    }
    return `<span style="${SLOT_BLANK_STYLE}" aria-hidden="true">&nbsp;</span>`;
  };

  if (haveMask && haveSurface && mask.length === surface.length) {
    const slots = Array.from({ length: surface.length }, (_, i) =>
      slotHtml(surface[i], mask[i] === 1)
    ).join('');
    return `<span style="${WORD_WRAPPER_STYLE}">${slots}</span>`;
  }

  // Legacy fallback: only the first letter is known. Render a single
  // visible slot — the legacy Mode A path always had acceptedSurfaceForm,
  // so this branch is defensive only.
  if (firstLetterFallback) {
    return `<span style="${WORD_WRAPPER_STYLE}">${slotHtml(firstLetterFallback, true)}</span>`;
  }
  return '';
}

// Correctness indicator — green check for first-shot good, muted check for
// recovered / right-word-wrong-form, red ring + X for the actual miss.
// Mirrors the at-a-glance status the MCQ + Stage 1 already render so the
// student has a consistent visual signal across question types.
function ResultIcon({ tier }) {
  if (tier === 'good' || tier === 'synonym_good') {
    return (
      <motion.div
        initial={{ scale: 0.7 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        className="inline-flex items-center justify-center w-8 h-8 rounded-full border-2 border-emerald-500 dark:border-emerald-400"
      >
        <Check className="w-4 h-4 text-emerald-600 dark:text-emerald-400" strokeWidth={2.5} />
      </motion.div>
    );
  }
  if (tier === 'hard_recovered' || tier === 'hard' || tier === 'synonym_recovered') {
    // Right idea — gray ring with a check. Used for second-try recovery
    // and for right-word-wrong-form (variant).
    return (
      <motion.div
        initial={{ scale: 0.7 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        className="inline-flex items-center justify-center w-8 h-8 rounded-full border-2 border-gray-400 dark:border-gray-500"
      >
        <Check className="w-4 h-4 text-gray-500 dark:text-gray-400" strokeWidth={2.5} />
      </motion.div>
    );
  }
  if (tier === 'hard_typo') {
    // Spelling miss — amber X says "close, but no" without the harshness
    // of the rose X used for an outright miss.
    return (
      <motion.div
        initial={{ scale: 0.7 }}
        animate={{ scale: 1 }}
        transition={{ type: 'spring', stiffness: 300, damping: 20 }}
        className="inline-flex items-center justify-center w-8 h-8 rounded-full border-2 border-amber-400 dark:border-amber-500"
      >
        <X className="w-4 h-4 text-amber-500 dark:text-amber-400" strokeWidth={2.5} />
      </motion.div>
    );
  }
  // 'again'
  return (
    <motion.div
      initial={{ scale: 0.7 }}
      animate={{ scale: 1 }}
      transition={{ type: 'spring', stiffness: 300, damping: 20 }}
      className="inline-flex items-center justify-center w-8 h-8 rounded-full border-2 border-rose-400 dark:border-rose-500"
    >
      <X className="w-4 h-4 text-rose-500 dark:text-rose-400" strokeWidth={2.5} />
    </motion.div>
  );
}
